import { UrlConfig } from 'config';
import { ApiResponse } from 'models/Api/ApiResponse';
import { AjaxService } from 'services';
import GroupingModel from 'models/grouping/GroupingModel';
import GroupingSummaryModel from 'models/grouping/GroupingSummaryModel';
import { RawGroupingData, RawGroupingDetailData } from 'models/grouping/types';
import { ProcessStatus } from 'models/types';
import { PaginationModel } from 'models';
import { RawPaginationData } from 'models/Pagination/Pagination';
import { JSONObject } from 'models/Api/types';

export class GroupingApiClient {
  public static async getGroupingById(id: string) {
    const url = UrlConfig.GROUPING.GROUPING_BY_ID + `${id}`;
    const response: ApiResponse<RawGroupingDetailData> = await AjaxService.getData(url);
    const grouping = new GroupingModel(response.data);
    return grouping;
  }

  public static async getGroupingsByStatus(status: ProcessStatus) {
    const url = UrlConfig.GROUPING.GROUPING_BY_STATUS + `${status}`;
    const response: ApiResponse<RawGroupingData[]> = await AjaxService.getData(url);
    const groupingList = response.data.map(rawGrouping => new GroupingSummaryModel(rawGrouping));
    return groupingList;
  }

  public static async getDetailedStatusGroupings(
    status: ProcessStatus,
    page?: number,
    perPage?: number
  ): Promise<PaginationModel<GroupingModel>> {
    const params = new URLSearchParams();
    if (page != null) params.set('page', String(page));
    if (perPage != null) params.set('per_page', String(perPage));
    const query = params.toString();
    const url =
      UrlConfig.GROUPING.GROUPING_DETAILED_STATUS +
      `${status}` +
      (query ? `?${query}` : '');
    const response: ApiResponse<RawPaginationData<RawGroupingDetailData>> = await AjaxService.getData(url);
    const paginatedResponse: PaginationModel<RawGroupingDetailData> = new PaginationModel(response.data);
    const formattedGroupings = paginatedResponse.data.map((rawGrouping: RawGroupingDetailData) => new GroupingModel(rawGrouping));
    const formattedPaginatedResponse: PaginationModel<GroupingModel> = new PaginationModel({ ...response.data, data: formattedGroupings, fetched: true });
    return formattedPaginatedResponse;
  }

  public static async getAllGroupings() {
    const url = UrlConfig.GROUPING.GROUPING_ALL;
    const response: ApiResponse<RawGroupingData[]> = await AjaxService.getData(url);
    const groupingList = response.data.map(rawGrouping => new GroupingSummaryModel(rawGrouping));
    return groupingList;
  }

  public static async createGrouping(payload: JSONObject) {
    const url = UrlConfig.GROUPING.BASE;
    const id: ApiResponse<string> = await AjaxService.postData(url, payload);
    return id.data;
  }

  public static async approveGrouping(payload: { grouping_id: string }) {
    const url = UrlConfig.GROUPING.GROUPING_APPROVE;
    const id = payload.grouping_id;
    await AjaxService.postData(url, payload);
    return id;
  }

  public static async rejectGrouping(payload: { grouping_id: string }) {
    const url = UrlConfig.GROUPING.GROUPING_REJECT;
    const id = payload.grouping_id;
    await AjaxService.postData(url, payload);
    return id;
  }

  public static async processGrouping(payload: { grouping_id: string }) {
    const url = UrlConfig.GROUPING.GROUPING_PROCESS;
    await AjaxService.postData(url, payload);
  }

  public static async addListingsToGrouping(id: string, payload: { listings: string[] }) {
    const url = UrlConfig.GROUPING.GROUPING_BY_ID + `${id}/add_listings`;
    await AjaxService.postData(url, payload);
    return id;
  }

  public static async removeListingsFromGrouping(id: string, payload: { listings: string[] }) {
    const url = UrlConfig.GROUPING.GROUPING_BY_ID + `${id}/remove_listings`;
    await AjaxService.postData(url, payload);
    return id;
  }
}
